/**
 * Life4Earth V2 — Pre-Flight Seed Data
 *
 * Demo pre-flight items for seed/demo projects.
 * Used as fallback when the API is unreachable.
 */

import type { PreflightItem } from './preflightTypes';

/* ------------------------------------------------------------------ */
/*  Helper: deterministic UUIDs for seed data                          */
/*  Backend requires proper Guid values — pf-001-style IDs will fail.  */
/* ------------------------------------------------------------------ */

/**
 * Deterministic seed UUID generator.
 * Produces valid v4-format UUIDs seeded from a numeric index,
 * so seed data items always have stable, reproducible IDs.
 */
function seedUUID(n: number): string {
  const hex = n.toString(16).padStart(12, '0');
  return `00000000-0000-4000-8000-${hex}`;
}

/* ------------------------------------------------------------------ */
/*  Project 1: Tree Planting Initiative                                */
/*  f3b1d569-688c-4756-fc75-ecdd332b3147                              */
/* ------------------------------------------------------------------ */

const PROJ1 = 'f3b1d569-688c-4756-fc75-ecdd332b3147';
const DEMO_USER = '00000000-0000-0000-0000-000000000001';

const proj1Items: PreflightItem[] = [
  {
    item_guid: seedUUID(1),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'knowledge',
    label: 'Certified arborist',
    description: 'At least one team member with arborist certification to oversee planting protocols.',
    requirement_type: 'threshold',
    target_quantity: 1,
    unit: 'people',
    current_quantity: 1,
    is_fulfilled: true,
    visibility: 'public',
    mission_subkpi_key: null,
    tags: ['certification', 'leadership'],
    sort_order: 1,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-01-22T14:30:00Z',
  },
  {
    item_guid: seedUUID(2),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'knowledge',
    label: 'Volunteers',
    description: 'Minimum volunteer count to execute the first planting phase.',
    requirement_type: 'threshold',
    target_quantity: 5,
    unit: 'people',
    current_quantity: 4,
    is_fulfilled: false,
    visibility: 'public',
    mission_subkpi_key: 'volunteers',
    tags: ['recruitment'],
    sort_order: 2,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-02-15T11:00:00Z',
  },
  {
    item_guid: seedUUID(3),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'materials',
    label: 'Tree seedlings',
    description: 'Native species seedlings sourced from approved nursery.',
    requirement_type: 'threshold',
    target_quantity: 50,
    unit: 'seedlings',
    current_quantity: 50,
    is_fulfilled: true,
    visibility: 'public',
    mission_subkpi_key: 'trees_planted',
    tags: ['native-species', 'nursery'],
    sort_order: 3,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-02-01T10:00:00Z',
  },
  {
    item_guid: seedUUID(4),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'equipment',
    label: 'Planting tools',
    description: 'Shovels, post-hole diggers, and protective gloves for the team.',
    requirement_type: 'binary',
    target_quantity: null,
    unit: null,
    current_quantity: 1,
    is_fulfilled: true,
    visibility: 'internal',
    tags: ['hand-tools'],
    sort_order: 4,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-01-28T16:00:00Z',
  },
  {
    item_guid: seedUUID(5),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'financial',
    label: 'Phase 1 funding',
    description: 'Minimum budget required to purchase seedlings and supplies for the first planting phase.',
    requirement_type: 'threshold',
    target_quantity: 2500,
    unit: 'USD',
    current_quantity: 2500,
    is_fulfilled: true,
    visibility: 'public',
    tags: ['budget', 'grants'],
    sort_order: 5,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-01-30T09:00:00Z',
  },
  {
    item_guid: seedUUID(6),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'admin_legal',
    label: 'Land-use permit',
    description: 'Municipal permit approving tree planting on the designated parcels.',
    requirement_type: 'binary',
    target_quantity: null,
    unit: null,
    current_quantity: 1,
    is_fulfilled: true,
    visibility: 'public',
    tags: ['permit', 'municipal'],
    sort_order: 6,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-01-20T12:00:00Z',
  },
  {
    item_guid: seedUUID(7),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'knowledge',
    label: 'Soil assessment report',
    description: 'Soil pH, drainage, and contamination tests for each planting site.',
    requirement_type: 'binary',
    target_quantity: null,
    unit: null,
    current_quantity: 1,
    is_fulfilled: true,
    visibility: 'internal',
    tags: ['testing', 'soil'],
    sort_order: 7,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-01-18T15:00:00Z',
  },
  {
    item_guid: seedUUID(8),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'facilities',
    label: 'Site selection finalized',
    description: 'All planting sites surveyed and confirmed with property owners.',
    requirement_type: 'binary',
    target_quantity: null,
    unit: null,
    current_quantity: 1,
    is_fulfilled: true,
    visibility: 'public',
    tags: ['site-survey'],
    sort_order: 8,
    created_by: DEMO_USER,
    created_at: '2026-01-10T09:00:00Z',
    updated_at: '2026-01-15T10:00:00Z',
  },
  {
    item_guid: seedUUID(9),
    project_guid: PROJ1,
    phase: 'preparation',
    category: 'financial',
    label: 'Corporate sponsor commitment',
    description: 'Matching donation from a corporate partner, contingent on full volunteer roster.',
    requirement_type: 'commitment',
    target_quantity: 5000,
    unit: 'USD',
    current_quantity: 0,
    is_fulfilled: false,
    visibility: 'public',
    tags: ['sponsor', 'matching-gift'],
    sort_order: 9,
    created_by: DEMO_USER,
    created_at: '2026-01-12T10:00:00Z',
    updated_at: '2026-02-20T08:00:00Z',
  },
];

/* ------------------------------------------------------------------ */
/*  Project 2: Coral Reef Restoration                                  */
/*  cf344fa3-2497-b57f-489a-dd77068c77ff                              */
/* ------------------------------------------------------------------ */

const PROJ2 = 'cf344fa3-2497-b57f-489a-dd77068c77ff';

const proj2Items: PreflightItem[] = [
  {
    item_guid: seedUUID(10),
    project_guid: PROJ2,
    phase: 'preparation',
    category: 'knowledge',
    label: 'SCUBA-certified divers',
    description: 'Minimum dive team for coral transplantation.',
    requirement_type: 'threshold',
    target_quantity: 3,
    unit: 'people',
    current_quantity: 2,
    is_fulfilled: false,
    visibility: 'public',
    tags: ['diving', 'certification'],
    sort_order: 1,
    created_by: DEMO_USER,
    created_at: '2026-01-20T10:00:00Z',
    updated_at: '2026-02-10T09:00:00Z',
  },
  {
    item_guid: seedUUID(11),
    project_guid: PROJ2,
    phase: 'preparation',
    category: 'admin_legal',
    label: 'Marine research permit',
    description: 'Government permit for underwater restoration work in the protected zone.',
    requirement_type: 'binary',
    target_quantity: null,
    unit: null,
    current_quantity: 0,
    is_fulfilled: false,
    visibility: 'public',
    tags: ['permit', 'marine'],
    sort_order: 2,
    created_by: DEMO_USER,
    created_at: '2026-01-20T10:00:00Z',
    updated_at: '2026-02-05T14:00:00Z',
  },
  {
    item_guid: seedUUID(12),
    project_guid: PROJ2,
    phase: 'preparation',
    category: 'materials',
    label: 'Coral fragments',
    description: 'Lab-grown coral fragments ready for transplantation.',
    requirement_type: 'threshold',
    target_quantity: 200,
    unit: 'fragments',
    current_quantity: 120,
    is_fulfilled: false,
    visibility: 'public',
    tags: ['coral', 'lab-grown'],
    sort_order: 3,
    created_by: DEMO_USER,
    created_at: '2026-01-20T10:00:00Z',
    updated_at: '2026-03-01T11:00:00Z',
  },
];

/* ------------------------------------------------------------------ */
/*  Combined export                                                    */
/* ------------------------------------------------------------------ */

export const preflightSeedData: PreflightItem[] = [
  ...proj1Items,
  ...proj2Items,
];
